import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { Waypoints, Loader2, Focus, RefreshCw, Play, Pause, History, X } from "lucide-react";
import { api } from "@/lib/api";

// AVA's Neocortex - the living knowledge graph rendered as cortical tissue.
// Concepts group into cortical "columns" by the domain that taught them, wired by
// soft dendritic arcs carrying signal particles; hub concepts pulse with activation
// ripples. Cool ice-glacier palette. Two interactions: (1) drill into a single
// neuron to spotlight its synaptic neighbours + trace which ships it wires across,
// and (2) a time-lapse that plays the cortex growing week over week.

const parseMs = (iso) => { const d = Date.parse(iso || ""); return isNaN(d) ? 0 : d; };
const DAY = 864e5;

function clusterOf(node) {
  const tags = node.source_projects || [];
  const git = tags.filter((t) => t !== "web-search");
  if (git.length) return git[0];
  if (tags.includes("web-search")) return "web research";
  return "AVA self";
}
function columnHue(index, total) { return Math.round(172 + (index / Math.max(1, total)) * 96); }

function makeClusterForce(anchors, strength) {
  let nodes;
  function force(alpha) {
    if (!nodes) return;
    for (const n of nodes) {
      const a = anchors[n.cluster];
      if (!a) continue;
      n.vx += (a.x - n.x) * strength * alpha;
      n.vy += (a.y - n.y) * strength * alpha;
    }
  }
  force.initialize = (ns) => { nodes = ns; };
  return force;
}

export default function Neocortex() {
  const wrapRef = useRef(null);
  const fgRef = useRef(null);
  const [raw, setRaw] = useState(null);
  const [stats, setStats] = useState(null);
  const [dims, setDims] = useState({ width: 800, height: 600 });
  const [loading, setLoading] = useState(true);
  const [activeCol, setActiveCol] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [timeLapse, setTimeLapse] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);

  const load = useCallback(() => {
    Promise.all([api.get("/brain/graph"), api.get("/brain/stats")])
      .then(([g, s]) => { setRaw(g.data); setStats(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((e) => setDims({ width: e[0].contentRect.width, height: e[0].contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { graphData, columns, anchors, maxVal, adj, nodeById, minTs, maxTs } = useMemo(() => {
    if (!raw || !raw.nodes) return { graphData: { nodes: [], links: [] }, columns: [], anchors: {}, maxVal: 1, adj: {}, nodeById: {}, minTs: 0, maxTs: 0 };
    const nodes = raw.nodes.map((n) => ({ ...n, cluster: clusterOf(n), createdMs: parseMs(n.ts) }));
    const uniq = Array.from(new Set(nodes.map((n) => n.cluster))).sort();
    const total = uniq.length;
    const sorted = [...nodes].sort((a, b) => (b.val || 0) - (a.val || 0));
    const hubIds = new Set(sorted.slice(0, Math.min(14, sorted.length)).map((n) => n.id));
    let mx = 1; for (const n of nodes) mx = Math.max(mx, n.val || 1);

    const countOf = {};
    for (const n of nodes) countOf[n.cluster] = (countOf[n.cluster] || 0) + 1;
    const byCount = [...uniq].sort((a, b) => (countOf[b] || 0) - (countOf[a] || 0));
    const golden = Math.PI * (3 - Math.sqrt(5));
    const spread = 44;
    const anchorMap = {};
    byCount.forEach((name, i) => {
      const radius = spread * Math.sqrt(i + 0.5);
      const theta = i * golden;
      anchorMap[name] = { x: Math.cos(theta) * radius, y: Math.sin(theta) * radius };
    });
    const cols = uniq.map((name) => ({
      name, hue: columnHue(uniq.indexOf(name), total), count: countOf[name] || 0,
      color: `hsl(${columnHue(uniq.indexOf(name), total)},44%,70%)`, anchor: anchorMap[name],
    }));
    const nodeById = {};
    for (const n of nodes) {
      n.hue = columnHue(uniq.indexOf(n.cluster), total);
      n.isHub = hubIds.has(n.id);
      n.rp = (n.id ? n.id.length * 137 : 0) % 3000;
      nodeById[n.id] = n;
    }
    cols.sort((a, b) => b.count - a.count);

    // Adjacency + timeline bounds from links; keep source/target as ids for the sim.
    const adj = {};
    const links = (raw.links || []).map((l) => {
      const cMs = parseMs(l.ts), lMs = parseMs(l.lr) || parseMs(l.ts);
      (adj[l.source] ||= []).push({ id: l.target, strength: l.value });
      (adj[l.target] ||= []).push({ id: l.source, strength: l.value });
      return { ...l, createdMs: cMs, lrMs: lMs };
    });
    let mn = Infinity, mxTs = 0;
    for (const n of nodes) if (n.createdMs > 0) { mn = Math.min(mn, n.createdMs); mxTs = Math.max(mxTs, n.createdMs); }
    for (const l of links) { const m = l.lrMs || l.createdMs; if (m > 0) mxTs = Math.max(mxTs, m); }
    if (!isFinite(mn)) mn = Date.now() - 7 * DAY;
    mxTs = Math.max(mxTs, Date.now());
    return { graphData: { nodes, links }, columns: cols, anchors: anchorMap, maxVal: mx, adj, nodeById, minTs: mn, maxTs: mxTs };
  }, [raw]);

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || !graphData.nodes.length) return;
    try {
      fg.d3Force("cluster", makeClusterForce(anchors, 0.16));
      fg.d3Force("charge")?.strength(-26);
      fg.d3Force("link")?.distance(26).strength(0.22);
      fg.d3ReheatSimulation();
      setTimeout(() => { try { fg.zoomToFit(600, 60); } catch (e) {} }, 1400);
    } catch (e) {}
  }, [graphData, anchors]);

  // Time-lapse playhead.
  useEffect(() => {
    if (!timeLapse || !playing) return;
    const span = Math.max(1, maxTs - minTs);
    const stepMs = 50, durationMs = 16000;
    const inc = span / (durationMs / stepMs);
    const id = setInterval(() => {
      setT((prev) => {
        const next = prev + inc;
        if (next >= maxTs) { setPlaying(false); return maxTs; }
        return next;
      });
    }, stepMs);
    return () => clearInterval(id);
  }, [timeLapse, playing, minTs, maxTs]);

  const recenter = () => { try { fgRef.current?.zoomToFit(700, 60); } catch (e) {} };

  const focusColumn = (name) => {
    setSelectedId(null);
    setActiveCol((prev) => {
      const next = prev === name ? null : name;
      const fg = fgRef.current;
      if (fg) {
        if (next) {
          const pts = graphData.nodes.filter((n) => n.cluster === next && typeof n.x === "number");
          if (pts.length) {
            const cx = pts.reduce((s, n) => s + n.x, 0) / pts.length;
            const cy = pts.reduce((s, n) => s + n.y, 0) / pts.length;
            try { fg.centerAt(cx, cy, 650); fg.zoom(3.4, 650); } catch (e) {}
          }
        } else { try { fg.zoomToFit(650, 60); } catch (e) {} }
      }
      return next;
    });
  };

  const selectNode = (node) => {
    if (timeLapse) return;
    setActiveCol(null);
    setSelectedId(node.id);
    const fg = fgRef.current;
    if (fg && typeof node.x === "number") { try { fg.centerAt(node.x, node.y, 550); fg.zoom(4.2, 550); } catch (e) {} }
  };

  const toggleTimeLapse = () => {
    setTimeLapse((on) => {
      const next = !on;
      setSelectedId(null); setActiveCol(null);
      if (next) { setT(minTs); setPlaying(true); setTimeout(() => { try { fgRef.current?.zoomToFit(600, 60); } catch (e) {} }, 100); }
      else { setPlaying(false); setT(maxTs); }
      return next;
    });
  };

  const neighbourSet = useMemo(() => new Set((selectedId && adj[selectedId] ? adj[selectedId] : []).map((x) => x.id)), [selectedId, adj]);
  const selectedNode = selectedId ? nodeById[selectedId] : null;
  const selectedNeighbours = useMemo(() => {
    if (!selectedId || !adj[selectedId]) return [];
    return [...adj[selectedId]].sort((a, b) => b.strength - a.strength).slice(0, 7)
      .map((x) => ({ id: x.id, strength: x.strength, ships: (nodeById[x.id]?.source_projects || []).filter((s) => s !== "web-search") }));
  }, [selectedId, adj, nodeById]);

  const linkVal = (l) => {
    const v = l.value || 0;
    if (!timeLapse) return v;
    if ((l.createdMs || 0) > t) return 0;
    const denom = ((l.lrMs || 0) - (l.createdMs || 0)) || 1;
    const prog = Math.max(0, Math.min(1, (t - (l.createdMs || 0)) / denom));
    return 0.3 + (v - 0.3) * prog;
  };
  const linkOn = (l) => {
    if (timeLapse) return (l.createdMs || 0) <= t;
    if (selectedId) { const s = l.source?.id ?? l.source, tt = l.target?.id ?? l.target; return s === selectedId || tt === selectedId; }
    if (activeCol) return l.source?.cluster === activeCol || l.target?.cluster === activeCol;
    return true;
  };

  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl relative overflow-hidden" data-testid="neocortex-view">
      <div className="absolute inset-0 pointer-events-none" style={{
        background:
          "radial-gradient(ellipse 40% 55% at 28% 34%, rgba(120,190,205,0.07), transparent 60%)," +
          "radial-gradient(ellipse 45% 50% at 74% 62%, rgba(150,170,220,0.06), transparent 60%)," +
          "linear-gradient(180deg, rgba(140,190,210,0.035) 0%, transparent 22%, transparent 78%, rgba(150,170,220,0.03) 100%)",
      }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
        backgroundImage: "repeating-linear-gradient(180deg, rgba(190,215,225,0.6) 0px, rgba(190,215,225,0.6) 1px, transparent 1px, transparent 84px)",
      }} />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-start justify-between gap-4 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center shrink-0">
            <Waypoints className="w-5 h-5 text-cyan-200/80" />
          </div>
          <div>
            <h2 className="font-display font-bold text-white text-lg leading-tight">Neocortex</h2>
            <p className="text-slate-400 text-xs mt-1 max-w-xl">
              AVA's living knowledge graph as cortical tissue. <span className="text-cyan-200/80">Click any neuron</span> to trace how a concept wires across ships, isolate a <span className="text-cyan-200/80">column</span> from the legend, or play the <span className="text-cyan-200/80">time-lapse</span> to watch the cortex grow.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {stats && (
            <div className="hidden lg:flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mr-1">
              <span><span className="text-cyan-200/80">{stats.total_nodes}</span> nodes</span>
              <span><span className="text-cyan-200/80">{stats.total_edges}</span> synapses</span>
              <span><span className="text-cyan-200/80">{columns.length}</span> columns</span>
            </div>
          )}
          <button onClick={toggleTimeLapse} data-testid="neocortex-timelapse-toggle" title="Time-lapse"
            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-[10px] font-mono-data uppercase tracking-wider transition-colors ${timeLapse ? "bg-cyan-400/20 text-cyan-100 border-cyan-400/40" : "bg-white/5 text-slate-300 border-white/10 hover:text-cyan-200"}`}>
            <History className="w-3.5 h-3.5" /> Time-Lapse
          </button>
          <button onClick={recenter} data-testid="neocortex-recenter" title="Recenter"
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-cyan-200 transition-colors">
            <Focus className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setLoading(true); load(); }} data-testid="neocortex-refresh" title="Refresh"
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-cyan-200 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Selected-neuron drill-in card */}
      {selectedNode && (
        <div className="absolute top-24 left-4 z-20 w-64 glass-panel rounded-xl border border-cyan-400/25 p-3.5 shadow-[0_0_40px_rgba(34,211,238,0.1)]" data-testid="neocortex-node-card">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-sm font-display font-semibold text-cyan-100 leading-tight">{selectedNode.label}</h3>
            <button onClick={() => setSelectedId(null)} data-testid="neocortex-node-card-close" className="text-slate-500 hover:text-white shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="text-[10px] font-mono-data text-slate-500 mb-2">
            fired <span className="text-cyan-200/80">{selectedNode.val}×</span> · {(adj[selectedId] || []).length} synapses
          </div>
          <div className="mb-2">
            <p className="text-[9px] font-mono-data uppercase tracking-wider text-slate-500 mb-1">Wired across ships</p>
            <div className="flex flex-wrap gap-1">
              {(selectedNode.source_projects || []).filter((s) => s !== "web-search").length === 0 ? (
                <span className="text-[10px] text-slate-500 italic">AVA's own association</span>
              ) : selectedNode.source_projects.filter((s) => s !== "web-search").map((s) => (
                <span key={s} className="text-[9px] font-mono-data text-cyan-200 bg-cyan-400/10 border border-cyan-400/20 rounded px-1.5 py-0.5">{s}</span>
              ))}
              {(selectedNode.source_projects || []).includes("web-search") && (
                <span className="text-[9px] font-mono-data text-violet-200 bg-violet-400/10 border border-violet-400/20 rounded px-1.5 py-0.5">web research</span>
              )}
            </div>
          </div>
          <p className="text-[9px] font-mono-data uppercase tracking-wider text-slate-500 mb-1">Strongest links</p>
          <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {selectedNeighbours.map((nb) => (
              <li key={nb.id}>
                <button onClick={() => selectNode({ id: nb.id, x: nodeById[nb.id]?.x, y: nodeById[nb.id]?.y })}
                  className="w-full text-left group">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-300 group-hover:text-cyan-200 truncate">{nb.id}</span>
                    <span className="text-[9px] font-mono-data text-slate-500">{nb.strength.toFixed(2)}</span>
                  </div>
                  <div className="h-1 rounded-full bg-white/5 mt-0.5 overflow-hidden">
                    <div className="h-full bg-cyan-400/50" style={{ width: `${Math.round(nb.strength * 100)}%` }} />
                  </div>
                  {nb.ships.length > 0 && <span className="text-[8px] font-mono-data text-slate-600">{nb.ships.slice(0, 2).join(", ")}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Time-lapse controls (replaces legend while active) */}
      {timeLapse ? (
        <div className="absolute bottom-3 left-4 right-4 z-10 flex items-center gap-3" data-testid="neocortex-timeline">
          <button onClick={() => (t >= maxTs ? (setT(minTs), setPlaying(true)) : setPlaying((p) => !p))}
            data-testid="neocortex-play" className="p-2 rounded-lg bg-cyan-400/15 border border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/25 transition-colors">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <input type="range" min={minTs} max={maxTs} value={t} step={Math.max(1, Math.round((maxTs - minTs) / 400))}
            onChange={(e) => { setPlaying(false); setT(Number(e.target.value)); }}
            data-testid="neocortex-timeline-slider" className="flex-1 accent-cyan-400 h-1" />
          <span className="text-[10px] font-mono-data text-cyan-200/80 w-28 text-right shrink-0">
            {new Date(t || minTs).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </span>
        </div>
      ) : (
        columns.length > 0 && (
          <div className="absolute bottom-3 left-4 right-4 z-10 flex flex-wrap items-center gap-x-2.5 gap-y-1 max-h-16 overflow-hidden" data-testid="neocortex-legend">
            {(activeCol || selectedId) && (
              <button onClick={() => { setActiveCol(null); setSelectedId(null); recenter(); }} data-testid="neocortex-clear-focus"
                className="text-[9px] font-mono-data uppercase tracking-wider text-cyan-200 bg-cyan-400/15 border border-cyan-400/30 rounded-full px-2 py-0.5 hover:bg-cyan-400/25 transition-colors">✕ Clear</button>
            )}
            {columns.slice(0, 16).map((c) => (
              <button key={c.name} data-testid={`neocortex-legend-${c.name}`} onClick={() => focusColumn(c.name)}
                className={`flex items-center gap-1.5 text-[9px] font-mono-data transition-opacity ${activeCol && activeCol !== c.name ? "opacity-35 hover:opacity-70" : "opacity-100"} ${activeCol === c.name ? "text-cyan-100" : "text-slate-400 hover:text-slate-200"}`}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color, boxShadow: `0 0 6px ${c.color}` }} />
                {c.name} <span className="text-slate-600">{c.count}</span>
              </button>
            ))}
          </div>
        )
      )}

      <div ref={wrapRef} className="absolute inset-0" data-testid="neocortex-canvas">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Mapping cortical tissue…</div>
        ) : graphData.nodes.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center"><p className="text-[12px] text-slate-500 font-mono-data text-center px-4">Cortex quiet.<br /><span className="text-slate-600">Engage AVA to grow the first columns.</span></p></div>
        ) : (
          <ForceGraph2D
            ref={fgRef}
            width={dims.width}
            height={dims.height}
            graphData={graphData}
            backgroundColor="rgba(0,0,0,0)"
            nodeLabel="label"
            cooldownTicks={220}
            d3AlphaDecay={0.018}
            d3VelocityDecay={0.34}
            onNodeClick={selectNode}
            onBackgroundClick={() => setSelectedId(null)}
            linkCurvature={0.28}
            linkColor={(l) => { const on = linkOn(l); const v = linkVal(l); const base = 0.06 + v * 0.32; return `rgba(150,196,214,${on ? base : base * 0.08})`; }}
            linkWidth={(l) => 0.4 + linkVal(l) * 2.0}
            linkDirectionalParticles={(l) => (linkOn(l) && linkVal(l) > 0.32 ? 2 : 0)}
            linkDirectionalParticleSpeed={(l) => 0.003 + linkVal(l) * 0.008}
            linkDirectionalParticleWidth={(l) => 0.8 + linkVal(l) * 1.4}
            linkDirectionalParticleColor={() => "rgba(210,235,235,0.55)"}
            nodeCanvasObject={(node, ctx, globalScale) => {
              if (typeof node.x !== "number" || !isFinite(node.x) || !isFinite(node.y)) return;
              if (timeLapse && (node.createdMs || 0) > t) return;
              const isSel = selectedId === node.id;
              const neigh = selectedId ? neighbourSet.has(node.id) : false;
              let dim = false;
              if (!timeLapse) {
                if (selectedId) dim = !(isSel || neigh);
                else if (activeCol) dim = node.cluster !== activeCol;
              }
              const a = dim ? 0.08 : 1;
              const norm = Math.min(1, (node.val || 1) / (maxVal || 1));
              const r = 2.4 + norm * 5.4;
              const hue = node.hue ?? 190;
              const soma = `hsla(${hue},46%,72%,`;
              const pulse = 1 + (0.1 + norm * 0.22) * Math.sin(Date.now() / 700 + (node.x || 0));
              if ((node.isHub || isSel) && !dim) {
                const phase = ((Date.now() + node.rp) % 2800) / 2800;
                ctx.beginPath();
                ctx.arc(node.x, node.y, r + phase * 30, 0, 2 * Math.PI);
                ctx.strokeStyle = `hsla(${hue},50%,80%,${(1 - phase) * (isSel ? 0.4 : 0.26)})`;
                ctx.lineWidth = isSel ? 0.8 : 0.5;
                ctx.stroke();
              }
              const grad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, r * pulse * 3.2);
              grad.addColorStop(0, soma + `${(0.18 + norm * 0.28) * a})`);
              grad.addColorStop(1, "rgba(0,0,0,0)");
              ctx.beginPath();
              ctx.arc(node.x, node.y, r * pulse * 3.2, 0, 2 * Math.PI);
              ctx.fillStyle = grad; ctx.fill();
              ctx.beginPath();
              ctx.arc(node.x, node.y, r * pulse, 0, 2 * Math.PI);
              ctx.fillStyle = soma + `${0.85 * a})`;
              ctx.shadowColor = `hsl(${hue},50%,70%)`;
              ctx.shadowBlur = dim ? 0 : (isSel ? 12 : 5 + norm * 5);
              ctx.fill(); ctx.shadowBlur = 0;
              if (isSel) { ctx.beginPath(); ctx.arc(node.x, node.y, r * pulse + 1.6, 0, 2 * Math.PI); ctx.strokeStyle = "rgba(233,245,247,0.9)"; ctx.lineWidth = 0.7; ctx.stroke(); }
              ctx.beginPath();
              ctx.arc(node.x, node.y, r * pulse * 0.32, 0, 2 * Math.PI);
              ctx.fillStyle = `rgba(233,245,247,${0.6 * a})`;
              ctx.fill();
              const showLabel = !dim && (isSel || neigh || globalScale > 2 || activeCol === node.cluster);
              if (showLabel) {
                ctx.font = "3px 'JetBrains Mono', monospace";
                ctx.fillStyle = isSel ? "rgba(236,254,255,0.95)" : "rgba(226,240,242,0.82)";
                ctx.textAlign = "center";
                ctx.fillText(node.label, node.x, node.y + r * pulse + 4.4);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
